// The packet view (/kpi/nba?view=packet; design/postgame_packet/PACKET_VIEW_PLAN.md): the whole packet for the
// dashboard's window, in packet order — the team report (ownership, full, or full with player pages), Team KPIs,
// Player KPIs, Rebounding — on one page that prints with the browser's own print. It composes the pages every other
// view uses (TeamReport, TeamKpiPage, PlayerKpiPage, ReboundingKpiPage) and adds none of its own: a window is data.
import { useCallback, useEffect, useMemo, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material";
import { getTeamFromString } from "../../../models/Team";
import { ExplorerEnvironment } from "../../../data/Cache";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import TeamReportComponent from "../../../TeamReport/TeamReport";
import TeamReportSkeleton from "../../../TeamReport/TeamReportSkeleton";
import {
  customBuildText,
  customReportId,
  reportSeason,
  reportSeasonType,
  usePlayerBlobs,
  useTeamReport,
} from "../../../TeamReport/useTeamReport";
import type { ExplorerAPI } from "../../../util/Requests";
import ReportFrame from "../TeamReportv2/ReportFrame";
import { Pending, useKpiData, WindowHead } from "./common";
import { asWindow } from "./kpiLib";
import PlayerKpiPage from "./PlayerKpiPage";
import { ReboundingKpiPage, ReboundingResponse } from "./ReboundingKpiPage";
import TeamKpiPage from "./TeamKpiPage";
import type { KpiResponse } from "./types";

export type PacketReport = "ownership" | "full" | "players";
export const PACKET_REPORTS: { key: PacketReport; label: string }[] = [
  { key: "ownership", label: "Ownership" },
  { key: "full", label: "Full" },
  { key: "players", label: "Full + players" },
];

const cleanTheme = createTheme();
const POR = getTeamFromString("POR", ExplorerEnvironment.NBA);

/** The report for the window: one game's, the season's, or a Last N window report (L5 / L10 / L20, built after each
 * game night). A custom pick's report is requested instead (useCustomReportId). */
function reportIdFor(w: string, data: KpiResponse | null | undefined) {
  const meta = data?.meta;
  if (!meta || !POR) return null;
  if (w === "game") {
    const id = meta.game?.id;
    return id ? `${POR.abbrev}-game-${id.replace(/^nba:/, "")}` : null;
  }
  if (w === "season") return `${POR.abbrev}-season-${meta.season}`;
  const last = /^last(5|10|20)$/.exec(w);
  if (last) return `${POR.abbrev}-L${last[1]}-${meta.season}`;
  return null;
}

/**
 * The custom pick's report id. The dashboard's pick changes as games are clicked, so it only peeks: a pick already
 * built (or being built) loads, any other waits for `build()`, which queues it on the cron box.
 */
function useCustomReportId(api: ExplorerAPI, picked: string[], season: number | undefined) {
  const sig = picked.join(",");
  const [state, setState] = useState<{ sig: string; id: string | null; error?: string } | null>(null);
  const ask = useCallback(
    (peek: boolean, isCancelled: () => boolean = () => false) => {
      if (!sig || !season || !POR) return;
      api
        .requestCustomReport(POR, season, sig.split(","), peek)
        .then(({ key, status }) => {
          if (isCancelled()) return;
          const id = customReportId(POR!.abbrev, key, season);
          setState({ sig, id: status === "none" || status === "failed" ? null : id });
        })
        .catch((err) => {
          if (!isCancelled())
            setState({ sig, id: null, error: err?.response?.data?.error ?? "Could not request the custom report." });
        });
    },
    [api, sig, season],
  );
  useEffect(() => {
    let cancelled = false;
    ask(true, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [ask]);
  const current = state?.sig === sig ? state : null;
  return { id: current?.id ?? null, error: current?.error, asked: !!current, build: () => ask(false) };
}

function ReportPart({
  reportId,
  variant,
  winLabel,
}: {
  reportId: string;
  variant: PacketReport;
  winLabel: string;
}) {
  const api = useLeagueApi();
  const {
    report,
    reportId: loadedId,
    error,
    building,
  } = useTeamReport(api, POR, reportId);
  const blobs = usePlayerBlobs(api, report, loadedId, variant === "players");
  const ownership = variant === "ownership";
  if (building) return <Pending title="Team report" reason={customBuildText(building)} />;
  if (error)
    return (
      <Pending
        title="Team report"
        reason={
          /-L\d+-/.test(reportId)
            ? `No ${winLabel} team report yet: it is built once POR has played that many games, after the game night.`
            : "Could not load the team report."
        }
      />
    );
  return (
    <ThemeProvider theme={cleanTheme}>
      <div className="packet-report">
        <ReportFrame fullPrintWidth={ownership}>
          {!report || loadedId !== reportId ? (
            <TeamReportSkeleton isOwnershipReport={ownership} />
          ) : (
            <TeamReportComponent
              {...report}
              playerBlobs={blobs ?? undefined}
              selectedReportId={loadedId}
              showOwnershipPage={ownership}
              teamId={POR?.nbaId?.toString()}
              season={reportSeason(loadedId)}
              seasonType={reportSeasonType(loadedId, report)}
              hideToc
            />
          )}
        </ReportFrame>
      </div>
    </ThemeProvider>
  );
}

export default function PacketView({
  query,
  w,
  winLabel,
  win,
  goalsLabel,
  variant,
  picked = [],
}: {
  /** The dashboard's API query for the window: `season=…&window=…[&through=…]`. */
  query: string;
  w: string;
  winLabel: string;
  win: WindowHead | null;
  goalsLabel?: string;
  variant: PacketReport;
  /** The custom window's games (core ids) */
  picked?: string[];
}) {
  const api = useLeagueApi();
  const team = useKpiData<KpiResponse>(`api/kpi/dashboard/team?${query}`);
  const players = useKpiData<KpiResponse>(`api/kpi/dashboard/players?${query}`);
  const reb = useKpiData<ReboundingResponse>(
    `api/kpi/dashboard/rebounding?${query}`,
  );
  const windowed = w !== "game";
  const playersData = useMemo(
    () => (players.data && windowed ? asWindow(players.data) : players.data),
    [players.data, windowed],
  );
  const custom = useCustomReportId(api, w === "custom" ? picked : [], team.data?.meta?.season);
  const reportId = w === "custom" ? custom.id : reportIdFor(w, team.data);
  const ready = (d: { status?: { state: string } } | null | undefined) =>
    d?.status?.state === "ready";
  return (
    <div className="packet-view">
      {reportId ? (
        <ReportPart reportId={reportId} variant={variant} winLabel={winLabel} />
      ) : (
        team.data && (
          <div className="kpi-doc packet-part">
            <Pending
              title="Team report"
              reason={
                w !== "custom" ? (
                  "No team report for this window."
                ) : custom.error ? (
                  custom.error
                ) : !custom.asked ? (
                  "Looking for a team report over these games…"
                ) : (
                  <>
                    No team report over these {picked.length} games yet.{" "}
                    <button className="kpi-link" onClick={custom.build}>
                      Build it
                    </button>{" "}
                    (about a minute; it opens here when ready).
                  </>
                )
              }
            />
          </div>
        )
      )}
      <div className="kpi-doc packet-part">
        {team.error && (
          <Pending
            title="Team KPIs"
            reason={`Could not load the KPIs: ${team.error}`}
          />
        )}
        {ready(team.data) && team.data!.meta.game && (
          <TeamKpiPage
            data={team.data!}
            windowed={windowed}
            // the packet geometry: one letter sheet, as the packet prints it
            pageLabel={`Team KPIs · ${winLabel}`}
            goalsLabel={goalsLabel}
          />
        )}
        {ready(playersData) && playersData!.meta.game && (
          <PlayerKpiPage
            data={playersData!}
            windowLabel={windowed ? winLabel : undefined}
            win={win}
            pageLabel={`Player KPIs · ${winLabel}`}
            goalsLabel={goalsLabel}
          />
        )}
        {ready(reb.data) && reb.data!.meta.game && (
          <ReboundingKpiPage
            data={reb.data!}
            win={win}
            pageLabel={`Rebounding · ${winLabel}`}
          />
        )}
        {reb.data?.status?.state === "pending" && (
          <Pending title="Rebounding" reason={reb.data.status.reason} />
        )}
      </div>
    </div>
  );
}
